import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { geocodingClient } from '../services/geocoding/geocodingClient';

function normalizeInput(value) {
  if (typeof value !== 'string') {
    return '';
  }
  return value;
}

export default function useGeocodingAutocomplete(options = {}) {
  const {
    enabled = true,
    minChars = 3,
    debounceMs = 300,
    countryCode,
    limit = 8,
    allowEmptyForwardFallback = false,
  } = options;

  const [inputValue, setInputValueState] = useState('');
  const [debouncedValue, setDebouncedValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const mountedRef = useRef(true);
  const requestSequenceRef = useRef(0);
  const abortControllerRef = useRef(null);
  const suppressedValueRef = useRef(null);
  const allowOpenRef = useRef(true);

  useEffect(() => () => {
    mountedRef.current = false;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const clearRequest = useCallback(() => {
    requestSequenceRef.current += 1;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const setInputValue = useCallback((value, config = {}) => {
    const normalizedValue = normalizeInput(value);
    const suppressLookup = config?.suppressLookup === true;

    if (suppressLookup) {
      allowOpenRef.current = false;
      suppressedValueRef.current = normalizedValue.trim();
      clearRequest();
      setDebouncedValue(normalizedValue.trim());
      setSuggestions([]);
      setIsOpen(false);
      setHighlightedIndex(-1);
      setIsLoading(false);
    } else {
      allowOpenRef.current = true;
      suppressedValueRef.current = null;
      clearRequest();
    }

    setInputValueState(normalizedValue);
    setError(null);
    setSelectedSuggestion((previous) => {
      if (!previous) {
        return null;
      }
      if (previous.label === normalizedValue) {
        return previous;
      }
      return null;
    });

    if (!suppressLookup && normalizedValue.trim().length >= minChars) {
      setSuggestions([]);
      setHighlightedIndex(-1);
      setIsOpen(false);
    }

    if (normalizedValue.trim().length < minChars) {
      setSuggestions([]);
      setIsOpen(false);
      setHighlightedIndex(-1);
      setIsLoading(false);
    }
  }, [clearRequest, minChars]);

  const clearSelection = useCallback(() => {
    setSelectedSuggestion(null);
  }, []);

  const clear = useCallback(() => {
    allowOpenRef.current = false;
    suppressedValueRef.current = null;
    clearRequest();
    setInputValueState('');
    setDebouncedValue('');
    setSuggestions([]);
    setIsLoading(false);
    setError(null);
    setSelectedSuggestion(null);
    setIsOpen(false);
    setHighlightedIndex(-1);
  }, [clearRequest]);

  const openDropdown = useCallback(() => {
    allowOpenRef.current = true;
    if (suggestions.length > 0) {
      setIsOpen(true);
    }
  }, [suggestions]);

  const closeDropdown = useCallback(() => {
    allowOpenRef.current = false;
    setIsOpen(false);
    setHighlightedIndex(-1);
  }, []);

  const selectSuggestion = useCallback((suggestion) => {
    if (!suggestion) {
      return null;
    }

    allowOpenRef.current = false;
    suppressedValueRef.current = (suggestion.label || '').trim();
    clearRequest();
    setSelectedSuggestion(suggestion);
    setInputValueState(suggestion.label || '');
    setDebouncedValue((suggestion.label || '').trim());
    setSuggestions([]);
    setIsLoading(false);
    setError(null);
    setIsOpen(false);
    setHighlightedIndex(-1);
    return suggestion;
  }, [clearRequest]);

  useEffect(() => {
    if (!enabled) {
      clearRequest();
      setSuggestions([]);
      setIsOpen(false);
      setIsLoading(false);
      setError(null);
      return undefined;
    }

    const trimmedInput = inputValue.trim();

    const suppressedValue = normalizeInput(suppressedValueRef.current).trim();
    if (suppressedValue && suppressedValue === trimmedInput) {
      return undefined;
    }

    const selectedLabel = (selectedSuggestion?.label || '').trim();
    if (selectedLabel && selectedLabel === trimmedInput) {
      return undefined;
    }

    if (trimmedInput.length < minChars) {
      clearRequest();
      setDebouncedValue(trimmedInput);
      setSuggestions([]);
      setIsOpen(false);
      setIsLoading(false);
      setError(null);
      return undefined;
    }

    const nextRequestSequence = requestSequenceRef.current + 1;
    requestSequenceRef.current = nextRequestSequence;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    const timeoutId = window.setTimeout(async () => {
      if (!mountedRef.current || requestSequenceRef.current !== nextRequestSequence) {
        return;
      }

      setDebouncedValue(trimmedInput);
      setIsLoading(true);
      setError(null);

      try {
        let nextSuggestions = await geocodingClient.forwardAutocomplete(
          {
            text: trimmedInput,
            countryCode,
            limit,
          },
          controller.signal
        );

        if (allowEmptyForwardFallback && nextSuggestions.length === 0) {
          try {
            // Best-effort lookup for address entry flows when autocomplete returns
            // a successful empty payload but forward search can still resolve the street.
            nextSuggestions = await geocodingClient.forwardLookup(
              {
                text: trimmedInput,
                countryCode,
                limit,
              },
              controller.signal
            );
          } catch (fallbackError) {
            if (fallbackError?.name === 'AbortError') {
              return;
            }
          }
        }

        if (!mountedRef.current || requestSequenceRef.current !== nextRequestSequence) {
          return;
        }

        setSuggestions(nextSuggestions);
        setHighlightedIndex(nextSuggestions.length > 0 ? 0 : -1);
        setIsOpen(allowOpenRef.current);
      } catch (requestError) {
        if (requestError?.name === 'AbortError') {
          return;
        }

        if (!mountedRef.current || requestSequenceRef.current !== nextRequestSequence) {
          return;
        }

        setSuggestions([]);
        setHighlightedIndex(-1);
        setIsOpen(false);
        setError(requestError);
      } finally {
        if (mountedRef.current && requestSequenceRef.current === nextRequestSequence) {
          setIsLoading(false);
        }
      }
    }, debounceMs);

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    };
  }, [
    inputValue,
    selectedSuggestion,
    enabled,
    minChars,
    debounceMs,
    countryCode,
    limit,
    allowEmptyForwardFallback,
    clearRequest,
  ]);

  const hasSuggestions = useMemo(() => suggestions.length > 0, [suggestions]);

  return {
    inputValue,
    debouncedValue,
    suggestions,
    isLoading,
    error,
    selectedSuggestion,
    isOpen,
    highlightedIndex,
    hasSuggestions,
    setInputValue,
    setHighlightedIndex,
    selectSuggestion,
    clearSelection,
    clear,
    openDropdown,
    closeDropdown,
  };
}
